/**
 * Pure rules for how the admin SPA presents a live manual-review assignment.
 *
 * The API is the enforcement boundary (`employee-review-decision.use-case.ts`). This module only
 * decides what to show: whose file it is, whether Accept/Reject should be disabled, and how to
 * split the queue into "mine" vs the waiting line.
 */

export type ManualReviewAssignmentState = 'QUEUED' | 'ASSIGNED' | string;

export type ManualReviewQueueItem = {
  applicationId: string;
  state: ManualReviewAssignmentState;
  queuedAt: string;
  assigneeEmail: string | null;
  assigneeDisplayName: string | null;
  assignedAt: string | null;
  assignmentCount: number;
  queuePosition: number | null;
  assignedToCaller?: boolean;
};

export type ManualReviewActability =
  | { kind: 'none' }
  | { kind: 'queued' }
  | { kind: 'mine'; label: string }
  | { kind: 'theirs'; label: string; assigneeEmail: string };

export function normalizeEmail(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

export function emailsMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeEmail(a);
  const right = normalizeEmail(b);
  return Boolean(left && right && left === right);
}

export function callerOwnsAssignment(
  item: Pick<ManualReviewQueueItem, 'assigneeEmail' | 'assignedToCaller'> | null | undefined,
  callerEmails: readonly (string | null | undefined)[],
): boolean {
  if (!item) return false;
  if (item.assignedToCaller === true) return true;
  const assignee = normalizeEmail(item.assigneeEmail);
  if (!assignee) return false;
  return callerEmails.some((email) => normalizeEmail(email) === assignee);
}

/**
 * Whether this caller may click Accept/Reject on a MANUAL_REVIEW application.
 *
 * No live assignment → allowed (covers files parked before dispatch shipped, and dispatch
 * failures). Queued → nobody owns it yet. Assigned to someone else → blocked. Assigned to the
 * caller → allowed.
 */
export function assignmentActability(
  assignment: ManualReviewQueueItem | null | undefined,
  callerEmails: readonly (string | null | undefined)[] = [],
): ManualReviewActability {
  if (!assignment) return { kind: 'none' };
  if (assignment.state === 'QUEUED' || !normalizeEmail(assignment.assigneeEmail)) {
    return { kind: 'queued' };
  }
  const name = assignment.assigneeDisplayName?.trim() || assignment.assigneeEmail || 'another reviewer';
  if (callerOwnsAssignment(assignment, callerEmails)) {
    return { kind: 'mine', label: name };
  }
  return {
    kind: 'theirs',
    label: name,
    assigneeEmail: normalizeEmail(assignment.assigneeEmail),
  };
}

export function actabilityMessage(act: ManualReviewActability): string | null {
  switch (act.kind) {
    case 'queued':
      return 'This loan is waiting in the review queue and is not yet assigned.';
    case 'theirs':
      return `Assigned to ${act.label} — only they can decide it.`;
    default:
      return null;
  }
}

export function splitReviewQueue(
  items: readonly ManualReviewQueueItem[],
  callerEmails: readonly (string | null | undefined)[] = [],
): {
  mine: ManualReviewQueueItem | null;
  assigned: ManualReviewQueueItem[];
  waiting: ManualReviewQueueItem[];
} {
  let mine: ManualReviewQueueItem | null = null;
  const assigned: ManualReviewQueueItem[] = [];
  const waiting: ManualReviewQueueItem[] = [];
  for (const item of items) {
    if (item.state === 'ASSIGNED' && callerOwnsAssignment(item, callerEmails)) {
      mine = item;
      continue;
    }
    if (item.state === 'ASSIGNED') {
      assigned.push(item);
      continue;
    }
    waiting.push(item);
  }
  return { mine, assigned, waiting };
}

export function formatShiftSince(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Whether the queue panel shows at all. A loan reviewer sees it even when the line is empty (so
 * "you do not have a file" is visible); so does a supervisor who can reassign, and anyone who
 * has a file. Everyone else sees it only while there is something in the line.
 */
export function shouldShowReviewQueuePanel(input: {
  itemCount: number;
  hasOwnFile: boolean;
  canReassign: boolean;
  isReviewer: boolean;
}): boolean {
  return input.itemCount > 0 || input.hasOwnFile || input.canReassign || input.isReviewer;
}
